import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../auth/context/AuthContext';
import { apiFetch } from '../../../shared/lib/apiClient';
import VendorReviewBanner from './VendorReviewBanner';
import IntegrationStatus from '../../integrations/components/IntegrationStatus';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt$ = (n) => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const fmtDate = (str) => {
  if (!str) return '—';
  return new Date(str + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getTimeOfDay = () => {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
};

const useCurrentTime = () => {
  const [time, setTime] = useState(() =>
    new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  );
  useEffect(() => {
    const interval = setInterval(() =>
      setTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }))
    , 60000);
    return () => clearInterval(interval);
  }, []);
  return time;
};

const STATUS_STYLE = {
  complete:   'bg-emerald-50 text-emerald-700',
  extracting: 'bg-amber-50 text-amber-600',
  extracted:  'bg-blue-50 text-blue-600',
  enhancing:  'bg-violet-50 text-violet-600',
  received:   'bg-zinc-100 text-zinc-500',
  failed:     'bg-red-50 text-red-500',
  error:      'bg-red-50 text-red-500',
};

const STATUS_LABEL = {
  complete: 'Complete', extracting: 'Extracting', extracted: 'Extracted',
  enhancing: 'Enriching', received: 'Received', failed: 'Failed', error: 'Error',
};

// ---------------------------------------------------------------------------
// Animated count-up
// ---------------------------------------------------------------------------
const useCountUp = (target, loaded, delay = 0) => {
  const [val, setVal] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!loaded || started.current || target == null) return;
    const t = setTimeout(() => {
      started.current = true;
      const dur = 1600;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min((now - start) / dur, 1);
        const e = 1 - Math.pow(1 - p, 3);
        setVal(e * target);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, delay);
    return () => clearTimeout(t);
  }, [loaded, target, delay]);
  return val;
};

// ---------------------------------------------------------------------------
// Sparkline
// ---------------------------------------------------------------------------
const Sparkline = ({ data, color = 'text-zinc-800', loaded, delay = 0 }) => {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => setDrawn(true), delay + 400);
    return () => clearTimeout(t);
  }, [loaded, delay]);

  if (!data || data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 100},${24 - ((v - min) / range) * 20}`);
  const path = `M ${pts.join(' L ')}`;
  const len = data.length * 15;
  return (
    <svg className={`w-full h-6 ${color}`} viewBox="0 0 100 24" preserveAspectRatio="none">
      <path d={path} fill="none" stroke="currentColor" strokeWidth="1.5"
        style={{ strokeDasharray: len, strokeDashoffset: drawn ? 0 : len, transition: 'stroke-dashoffset 1.4s cubic-bezier(0.16,1,0.3,1)' }} />
    </svg>
  );
};

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
const StatCard = ({ label, value, formatted, subtext, sparkData, loaded, delay = 0 }) => {
  const count = useCountUp(value, loaded, delay + 200);
  const display = formatted ? formatted(count) : Math.round(count).toLocaleString();

  return (
    <div
      className="bg-white border border-zinc-100 p-5 rounded-xl transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
      style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: `${delay}ms` }}
    >
      <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-3">{label}</p>
      <p className="text-3xl font-light text-zinc-900 tabular-nums mb-1">{display}</p>
      {subtext && <p className="text-[11px] text-zinc-400 mb-3">{subtext}</p>}
      {sparkData && <Sparkline data={sparkData} loaded={loaded} delay={delay} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Monthly spend chart (SVG area chart)
// ---------------------------------------------------------------------------
const SpendChart = ({ months, loaded }) => {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    if (!loaded) return;
    const t = setTimeout(() => setDrawn(true), 900);
    return () => clearTimeout(t);
  }, [loaded]);

  if (!months || months.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-sm text-zinc-300">
        No spend data yet
      </div>
    );
  }

  const values = months.map((m) => m.total);
  const max = Math.max(...values) || 1;
  const W = 600; const H = 140; const PAD = { t: 10, b: 24, l: 8, r: 8 };
  const iW = W - PAD.l - PAD.r;
  const iH = H - PAD.t - PAD.b;

  const pts = values.map((v, i) => ({
    x: PAD.l + (i / Math.max(values.length - 1, 1)) * iW,
    y: PAD.t + (1 - v / max) * iH,
  }));

  const linePath = pts.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x},${p.y}`;
    const prev = pts[i - 1];
    const cpx = (prev.x + p.x) / 2;
    return `${acc} Q ${cpx},${prev.y} ${p.x},${p.y}`;
  }, '');

  const areaPath = `${linePath} L ${pts[pts.length - 1].x},${PAD.t + iH} L ${pts[0].x},${PAD.t + iH} Z`;
  const lineLen = pts.length * 80;

  return (
    <div className="relative">
      <svg className="w-full" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#18181b" stopOpacity="0.08" />
            <stop offset="100%" stopColor="#18181b" stopOpacity="0" />
          </linearGradient>
          <clipPath id="spendClip">
            <rect x="0" y="0" width={W} height={H} />
          </clipPath>
        </defs>

        {/* Grid lines */}
        {[0.25, 0.5, 0.75, 1].map((pct) => (
          <line key={pct} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + pct * iH} y2={PAD.t + pct * iH}
            stroke="#f4f4f5" strokeWidth="1" />
        ))}

        {/* Area fill */}
        <path d={areaPath} fill="url(#spendGrad)"
          style={{ opacity: drawn ? 1 : 0, transition: 'opacity 1s ease' }} />

        {/* Line */}
        <path d={linePath} fill="none" stroke="#18181b" strokeWidth="1.5" clipPath="url(#spendClip)"
          style={{ strokeDasharray: lineLen, strokeDashoffset: drawn ? 0 : lineLen, transition: 'stroke-dashoffset 1.6s cubic-bezier(0.16,1,0.3,1)' }} />

        {/* Dots */}
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="3" fill="white" stroke="#18181b" strokeWidth="1.5"
            style={{ opacity: drawn ? 1 : 0, transition: `opacity 0.3s ease ${0.8 + i * 0.05}s` }} />
        ))}

        {/* X labels */}
        {months.map((m, i) => (
          <text key={i} x={pts[i].x} y={H - 4} textAnchor="middle" fontSize="9" fill="#a1a1aa">
            {m.month}
          </text>
        ))}
      </svg>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Top vendors bar list
// ---------------------------------------------------------------------------
const TopVendors = ({ vendors, loaded }) => {
  if (!vendors || vendors.length === 0) {
    return <p className="text-sm text-zinc-300 py-4 text-center">No vendor data yet</p>;
  }
  const max = vendors[0]?.total || 1;
  return (
    <div className="space-y-3">
      {vendors.slice(0, 6).map((v, i) => (
        <div key={v.name}
          className="transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateX(0)' : 'translateX(-8px)', transitionDelay: `${800 + i * 60}ms` }}
        >
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-zinc-700 truncate max-w-[180px]">{v.name}</span>
            <span className="text-xs text-zinc-500 tabular-nums ml-2 shrink-0">{fmt$(v.total)}</span>
          </div>
          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-zinc-800 rounded-full transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{ width: loaded ? `${(v.total / max) * 100}%` : '0%', transitionDelay: `${900 + i * 60}ms` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Recent invoices feed
// ---------------------------------------------------------------------------
const RecentInvoices = ({ invoices, loaded, onNavigate }) => {
  if (!invoices || invoices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-center">
        <p className="text-sm text-zinc-400">No invoices yet</p>
        <button onClick={() => onNavigate('documents')} className="mt-2 text-xs text-zinc-500 underline underline-offset-2 hover:text-zinc-800 transition-colors">
          Upload your first invoice
        </button>
      </div>
    );
  }

  return (
    <div className="divide-y divide-zinc-50">
      {invoices.slice(0, 8).map((inv, i) => {
        const isActive = !['complete', 'failed', 'error'].includes(inv.processing_status);
        return (
          <div key={inv.id}
            className="flex items-center gap-3 px-5 py-3 hover:bg-zinc-50/60 transition-colors duration-100"
            style={{ opacity: loaded ? 1 : 0, transition: `opacity 0.5s ease ${600 + i * 50}ms` }}
          >
            {/* File icon */}
            <div className="w-7 h-7 rounded-md bg-zinc-100 flex items-center justify-center shrink-0">
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-zinc-400">
                <path d="M8 1H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V5L8 1Z" />
                <path d="M8 1v4h4" />
              </svg>
            </div>

            {/* Vendor + file */}
            <div className="flex-1 min-w-0">
              <p className="text-sm text-zinc-800 truncate">{inv.vendor_name || inv.file_name}</p>
              <p className="text-[11px] text-zinc-400">{fmtDate(inv.invoice_date || inv.created_at)}</p>
            </div>

            {/* Amount */}
            <span className="text-sm font-medium text-zinc-800 tabular-nums shrink-0">
              {fmt$(inv.total_amount)}
            </span>

            {/* Status */}
            <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-lg font-medium shrink-0 ${STATUS_STYLE[inv.processing_status] || STATUS_STYLE.received}`}>
              {isActive && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />}
              {STATUS_LABEL[inv.processing_status] || inv.processing_status}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Category breakdown pills
// ---------------------------------------------------------------------------
const CategoryBreakdown = ({ categories, totalSpend, loaded }) => {
  if (!categories || categories.length === 0) return null;
  const COLORS = ['bg-zinc-900', 'bg-zinc-700', 'bg-zinc-500', 'bg-zinc-400', 'bg-zinc-300', 'bg-zinc-200'];

  return (
    <div>
      {/* Stacked bar */}
      <div className="flex h-2 rounded-full overflow-hidden gap-px mb-4">
        {categories.slice(0, 6).map((c, i) => (
          <div key={c.name}
            className={`${COLORS[i] || 'bg-zinc-200'} transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]`}
            style={{ width: loaded ? `${(c.total / totalSpend) * 100}%` : '0%', transitionDelay: `${700 + i * 80}ms` }}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {categories.slice(0, 6).map((c, i) => (
          <div key={c.name} className="flex items-center gap-1.5"
            style={{ opacity: loaded ? 1 : 0, transition: `opacity 0.4s ease ${800 + i * 60}ms` }}>
            <span className={`w-2 h-2 rounded-full shrink-0 ${COLORS[i] || 'bg-zinc-200'}`} />
            <span className="text-[11px] text-zinc-500">{c.name}</span>
            <span className="text-[11px] text-zinc-400 tabular-nums">{fmt$(c.total)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main OverviewView
// ---------------------------------------------------------------------------
const OverviewOldView = ({ loaded, onNavigate }) => {
  const { user, profile, activeCompany } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [invoices, setInvoices]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const currentTime = useCurrentTime();

  const firstName = profile?.full_name?.split(' ')[0] || user?.name?.split(' ')[0] || 'there';

  const load = useCallback(async () => {
    if (!activeCompany?.companyId) return;
    try {
      const [analyticsRes, invoicesRes] = await Promise.all([
        apiFetch('/api/invoices-v2/spend-analytics', { companyId: activeCompany.companyId }),
        apiFetch('/api/invoices-v2', { companyId: activeCompany.companyId }),
      ]);
      const [analyticsData, invoicesData] = await Promise.all([
        analyticsRes.ok ? analyticsRes.json() : null,
        invoicesRes.ok  ? invoicesRes.json()  : null,
      ]);
      if (analyticsData) setAnalytics(analyticsData);
      if (invoicesData)  setInvoices(invoicesData.invoices || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [activeCompany?.companyId]);

  useEffect(() => { load(); }, [load]);

  // Derived stats
  const totalSpend   = analytics?.summary?.totalSpend ?? 0;
  const invoiceCount = analytics?.summary?.invoiceCount ?? invoices.length;
  const vendorCount  = analytics?.summary?.vendorCount ?? 0;
  const categories   = analytics?.categoryBreakdown ?? [];
  const topVendors   = analytics?.topVendors ?? [];
  const monthlyData  = analytics?.spendOverTime ?? [];
  const processingCount = invoices.filter((i) => !['complete', 'failed', 'error'].includes(i.processing_status)).length;

  // Monthly spend sparkline for total spend card
  const spendSpark = monthlyData.slice(-10).map((m) => m.total);

  return (
    <div className="max-w-[1400px] mx-auto space-y-6">

      {/* ── Greeting ── */}
      <div
        className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '300ms' }}
      >
        <h1 className="text-2xl font-light text-zinc-900 mb-0.5">
          Good {getTimeOfDay()}, {firstName}
        </h1>
        <p className="text-sm text-zinc-400">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })} · {currentTime}
          {processingCount > 0 && (
            <span className="ml-3 inline-flex items-center gap-1.5 text-violet-500">
              <span className="w-1.5 h-1.5 rounded-full bg-violet-500 animate-pulse" />
              {processingCount} invoice{processingCount !== 1 ? 's' : ''} processing
            </span>
          )}
        </p>
      </div>

      {/* ── Vendor review banner ── */}
      <VendorReviewBanner loaded={loaded} />

      {/* ── Stat cards ── */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[0,1,2,3].map((i) => (
            <div key={i} className="bg-white border border-zinc-100 rounded-xl p-5 animate-pulse">
              <div className="h-2.5 w-16 bg-zinc-100 rounded mb-4" />
              <div className="h-8 w-24 bg-zinc-100 rounded mb-3" />
              <div className="h-2 w-full bg-zinc-50 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            label="Total Spend"
            value={totalSpend}
            formatted={(v) => fmt$(v)}
            subtext={monthlyData.length > 0 ? `across ${monthlyData.length} months` : undefined}
            sparkData={spendSpark.length >= 2 ? spendSpark : undefined}
            loaded={loaded}
            delay={400}
          />
          <StatCard
            label="Invoices"
            value={invoiceCount}
            formatted={(v) => Math.round(v).toLocaleString()}
            subtext="processed"
            loaded={loaded}
            delay={500}
          />
          <StatCard
            label="Vendors"
            value={vendorCount}
            formatted={(v) => Math.round(v).toLocaleString()}
            subtext="unique suppliers"
            loaded={loaded}
            delay={600}
          />
          <StatCard
            label="Categories"
            value={categories.length}
            formatted={(v) => Math.round(v).toLocaleString()}
            subtext="spend categories"
            loaded={loaded}
            delay={700}
          />
        </div>
      )}

      {/* ── Spend chart + Top vendors ── */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Spend over time */}
        <div
          className="lg:col-span-3 bg-white border border-zinc-100 rounded-xl p-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '700ms' }}
        >
          <div className="flex items-start justify-between mb-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-1">Monthly Spend</p>
              <p className="text-2xl font-light text-zinc-900 tabular-nums">{fmt$(totalSpend)}</p>
              {monthlyData.length > 0 && (
                <p className="text-[11px] text-zinc-400 mt-0.5">
                  {monthlyData[0]?.month} — {monthlyData[monthlyData.length - 1]?.month}
                </p>
              )}
            </div>
          </div>
          <SpendChart months={monthlyData} loaded={loaded} />
        </div>

        {/* Top vendors */}
        <div
          className="lg:col-span-2 bg-white border border-zinc-100 rounded-xl p-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '800ms' }}
        >
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-5">Top Vendors</p>
          <TopVendors vendors={topVendors} loaded={loaded} />
        </div>
      </div>

      {/* ── Recent invoices + Category breakdown + Integrations ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recent invoices */}
        <div
          className="lg:col-span-2 bg-white border border-zinc-100 rounded-xl overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '900ms' }}
        >
          <div className="px-5 py-4 border-b border-zinc-100 flex items-center justify-between">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Recent Invoices</p>
            <button onClick={() => onNavigate('documents')} className="text-xs text-zinc-400 hover:text-zinc-900 transition-colors">
              View all →
            </button>
          </div>
          <RecentInvoices invoices={invoices} loaded={loaded} onNavigate={onNavigate} />
        </div>

        {/* Right column: categories + integrations */}
        <div className="space-y-4">
          {/* Category breakdown */}
          {categories.length > 0 && (
            <div
              className="bg-white border border-zinc-100 rounded-xl p-5 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
              style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '1000ms' }}
            >
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 mb-4">Spend by Category</p>
              <CategoryBreakdown categories={categories} totalSpend={totalSpend} loaded={loaded} />
            </div>
          )}

          {/* Integrations status */}
          <div
            className="transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(16px)', transitionDelay: '1100ms' }}
          >
            <IntegrationStatus loaded={loaded} onNavigateToIntegrations={() => onNavigate('integrations')} />
          </div>
        </div>
      </div>

    </div>
  );
};

export default OverviewOldView;
